'use client';

import { type ReactNode } from 'react';

import { ProviderPageLayout } from '@paction-2.0/ui';

import { ProviderPageHeader } from './ProviderPageHeader';
import styles from './tileViewLayout.module.scss';

export type TileViewLayoutProps = {
  /** Page title */
  title: string;
  /** Optional subtitle or description */
  subtitle?: string;
  /** Error message to display */
  error?: string | null;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Loading state content (defaults to "Loading...") */
  loadingContent?: ReactNode;
  /** Empty state content (shown when no children and not loading) */
  emptyContent?: ReactNode;
  /** Optional tabs or filters to render below title */
  headerContent?: ReactNode;
  /** Optional actions to render in header (e.g., "Add" button) */
  headerActions?: ReactNode;
  /** The tile grid content */
  children: ReactNode;
};

/**
 * TileViewLayout - Standard layout for provider tile grid pages.
 *
 * Provides consistent structure for tile-based pages with:
 * - Title and optional subtitle
 * - Error display
 * - Loading state
 * - Optional header content (tabs, filters)
 * - Optional header actions
 *
 * @example
 * ```tsx
 * <TileViewLayout
 *   title="Waitlists"
 *   error={error}
 *   isLoading={isLoading}
 *   headerActions={<Button onClick={handleAdd}>Add Waitlist</Button>}
 * >
 *   <TileGrid>
 *     {items.map(item => <Tile key={item.id} ... />)}
 *   </TileGrid>
 * </TileViewLayout>
 * ```
 */
export function TileViewLayout({
  title,
  subtitle,
  error,
  isLoading = false,
  loadingContent,
  emptyContent,
  headerContent,
  headerActions,
  children
}: TileViewLayoutProps) {
  return (
    <ProviderPageLayout>
      <ProviderPageHeader
        title={title}
        subtitle={subtitle}
        actions={headerActions}
      />

      {headerContent && (
        <div className={styles.headerContent}>{headerContent}</div>
      )}

      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}

      {isLoading ? (
        <div className={styles.loadingState}>
          {loadingContent ?? <p className={styles.loadingText}>Loading...</p>}
        </div>
      ) : (
        children
      )}

      {!isLoading && emptyContent && !children && (
        <div className={styles.emptyState}>{emptyContent}</div>
      )}
    </ProviderPageLayout>
  );
}

export type TileGridProps = {
  children: ReactNode;
  className?: string;
};

/**
 * TileGrid - Flex-wrap container for tiles.
 *
 * @example
 * ```tsx
 * <TileGrid>
 *   <AddPlaceholderTile label="Add Item" onClick={handleAdd} />
 *   {items.map(item => <Tile key={item.id} ... />)}
 * </TileGrid>
 * ```
 */
export function TileGrid({ children, className }: TileGridProps) {
  return (
    <div className={`${styles.tileGrid} ${className ?? ''}`}>{children}</div>
  );
}
